<script>
import partnerNames from '@/mixins/partnerNames';
import partnerChoices from '@/mixins/partnerChoices';
import {hasEditPermission} from '@/mixins/roleLevelPermissions';
import { mapState } from 'vuex';
import {currentRoleLevelName} from '@/mixins/roleLevelPermissions';

export default {
	mixins: [partnerNames, partnerChoices],
	props: ['agreementData'],
	data() {
		return {
			isViewMode : !hasEditPermission("section-final-clauses"),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		};
	},
	computed: {
		...mapState(['currentRoleLevel']),
		getConclusions() {
			return this.agreementData.conclusions;
		},
		responsibleForLegalAdviceOptions() {
			return [
				{   text: this.isUserSeparatingParty? 'Each of us will be responsible for our own legal costs':'Each party is responsible for their own costs',
					value: 'each-party',
				},
				{
					text: this.isUserSeparatingParty? 'We will share the overall costs equally':'Parties will share overall costs incurred equally',
					value: 'share-costs',
				},
				{
					text: 'None',
					value: 'none',
				}];
		},
		disputeResolutionProcessOptions(){
			return [
				{ text: 'Mediation', value: 'mediation' },
				{ text: 'Arbitration', value: 'arbitration' },
				{
					text: 'Collaborative Family Law',
					value: 'collaborative-family-law',
				},
				{
					text: 'Supreme Court Proceedings',
					value: 'supreme-court-proceedings',
				},
				{
					text: 'Provincial and Supreme Court Proceedings',
					value: 'provincial-and-supreme-court-proceedings',
				},
				{
					text: 'None',
					value: 'none',
				},
			];
		},
		divorcePaymentOptions(){
			let options=[{
							text: this.isUserSeparatingParty? 'We will share the cost' : 'The parties will share the cost',
							value: 'shared',
						}];
			if(this.agreementData.conclusions.divorceApplication=='either'){
				options.push({
							text: this.isUserSeparatingParty? 'The person who applied for the Divorce' : 'The party who applied for the Divorce',
							value: 'appliedParty',
						})
			}
			return options
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					disputeResolutionProcess: "What dispute resolution process will you use if a dispute arises?",
					durationToPerformUnderAgreementAfterSigning: "How long will you each have to perform under the Agreement from the date the Agreement is signed?",
					divorceApplication: "Which of you will apply for a divorce?",
				}
			}
			else{
				return {
					disputeResolutionProcess: "What dispute resolution process will the parties use if a dispute arises?",
					durationToPerformUnderAgreementAfterSigning: "How long will the parties have to perform under the agreement from the date the Agreement is signed?",
					divorceApplication: "Which party will apply for divorce?",
				}
			}
		}
	},
};
</script>


<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Conclusions</h4>
			<a href="#" onclick="Intercom('showArticle', 11106912)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>
		<radio-group
			v-model="getConclusions.disputeResolutionProcess"
			class="my-3"
			:agreement-data="agreementData"
			input-cols="4"
			:label="dynamicQuestions.disputeResolutionProcess"
			select
			:options="disputeResolutionProcessOptions"
			testid="dispute-resolution-process"
			:length="'long'"
			:disabled="isViewMode"
			:toolTipText="`The most common choice is mediation. It's generally seen as the
			first collaborative step to finding resolution before court. Choosing mediation
			as a dispute resolution process doesn't mean that you can't go to court if
			needed, it's just stipulating that mediation is a first step before court.`"
		/>
		<radio-group
			v-model="getConclusions.responsibleForLegalAdvice"
			class="my-3"
			:agreement-data="agreementData"
			input-cols="4"
			label="Who is responsible for the costs of Independent Legal Advice?"
			select
			partner
			:options="responsibleForLegalAdviceOptions"
			testid="responsible-for-legal-advice"
			:length="'long'"
			:disabled="isViewMode"
			:toolTipText="`Generally, each party will be responsible for their own legal
			fees in obtaining independent legal advice, but you may outline any other
			arrangements.`"
		/>
		<input-group
			v-model="getConclusions.durationToPerformUnderAgreementAfterSigning"
			class="my-3"
			reset-undefined
			input-cols="4"
			:label="dynamicQuestions.durationToPerformUnderAgreementAfterSigning"
			placeholder="30 days"
			testid="duration-to-perform-under-agreement-after-signing"
			:disabled="isViewMode"
			:toolTipText="`Many parts of your Agreement will be performed or paid 'when the
			Agreement is signed'. This is a defined term. You get to choose what it means.
			Typically it means 'within 30 days of the Agreement being signed' but you may
			need more time and choose 45, 60, 90 days, etc.`"
		/>
		<input-group
			v-model="getConclusions.disclosureAndCompensationBeRequired"
			class="my-3"
			reset-undefined
			label="At what threshold amount will disclosure and compensation become required?"
			dollar
			placeholder="1,000"
			testid="disclosure-and-compensation-be-required"
			:disabled="isViewMode"
			v-if="agreementData.agreement.sections.includes('Property')"
			:toolTipText="`In every Agreement, there is provision for what happens if either
			spouse fails to disclose an asset. The threshold amount means that if your
			Agreement leaves out an asset over a certain amount (say, $1,000.00, which is
			most common but you can choose any amount) the spouse who owns it will have to
			disclose the details and compensate the other with a payment worth one-half of
			the value of the undisclosed asset.`"
		/>
		<conditional-group
			class="my-3"
			:condition="agreementData.relationship.married"
		>
			<b-form-group
				id="input-divorceApplication"
				input-cols="8"
				:label="dynamicQuestions.divorceApplication"
				label-for="divorceApplication"
			>
				<b-form-select
					id="divorceApplication"
					v-model="getConclusions.divorceApplication"
					size="sm"
					:options="[
						...partnerChoices,
						{ text: 'Either', value: 'either' },
					]"
					data-testid='relationship-divorce-application'
					class="col-lg-8"
					:disabled="isViewMode"
				/>
				<b-icon-info-circle class="ml-2 mt-2"
					v-if="isUserSeparatingParty"
					v-b-tooltip.html="`It is common for spouse who negotiate Agreements together to
					apply jointly for divorce once you have a Separation Agreement signed. Many
					couples bargain on this item, though, and can offer to take on the
					responsibility of filing and paying for the divorce application.`">
				</b-icon-info-circle>
			</b-form-group>

			<b-form-group
				id="input-divorcePayment"
				input-cols="8"
				label="Who will pay for the divorce application?"
				label-for="divorcePayment"
			>
				<b-form-select
					id="divorcePayment"
					v-model="getConclusions.divorcePayment"
					size="sm"
					:options="[
						...partnerChoices,
						...divorcePaymentOptions]"
					data-testid='relationship-divorce-payment'
					class="col-lg-8"
					:disabled="isViewMode"
					:class="{ 'custom-disabled': isViewMode }"
				/>
			</b-form-group>
		</conditional-group>
	</b-card>
</template>
